import { useEffect, useId, useRef, useState } from 'react';
import type { ApiError, HoldVital, MedPassNeed, MedPassNeeds, MedPassRow, MedScanBody, MedScanResult, OnShiftPerson } from '@aithora/shared';
import { ErrorNote } from '../../ErrorNote';
import { sx } from '../../ui/designStyles';
import { VITAL, vitalsOf } from './nurse/ScanSheet';
import { minsText } from './model';

/**
 * The med pass's one sheet (AF-1611, Andrew 2026-10-08), never a generic "are you sure":
 *   - 'given': only when the order needs more before Given is recorded (the server's 422):
 *     the reading (with the order's own hold rule, and "Not given: held per order"), the
 *     controlled count or witness, the PRN reason, or why it is late or early;
 *   - 'notgiven': Not given always needs a reason;
 *   - 'strike': after the 10-second Undo, the only fix; the original stays, struck;
 *   - 'refused': the building's medication rules do not let this person record this dose
 *     (the server's 403, med-rules/), or the order's service level does not take this record
 *     (409): the server's own reason and the rule it cites;
 *   - assisted (an order set to Assistance, 2026-10-13): 'given' and 'notgiven' record an
 *     Assisted outcome (taken, refused, missed); never Held per order, which is a nurse's call;
 *   - 'observe': the only record on a self-administered order, an observation note.
 */

/** The reasons a dose is not given, and what each records (the server's NOT_GIVEN_REASONS). */
export const NOT_GIVEN: readonly { code: string; label: string; status: 'MISSED' | 'REFUSED' | 'HELD' }[] = [
  { code: 'refused', label: 'Refused', status: 'REFUSED' },
  { code: 'held_per_order', label: 'Held per order', status: 'HELD' },
  { code: 'asleep', label: 'Asleep', status: 'MISSED' },
  { code: 'away', label: 'Away from the building', status: 'MISSED' },
  { code: 'unavailable', label: 'Medicine not available', status: 'MISSED' },
  { code: 'nothing_by_mouth', label: 'Nothing by mouth', status: 'MISSED' },
  { code: 'other', label: 'Other', status: 'MISSED' },
];
/** Why a recorded dose is struck out (the server's STRIKE_REASONS). */
export const STRIKE: readonly string[] = ['Wrong resident', 'Wrong medicine', 'Recorded by mistake', 'Not actually given', 'Wrong time', 'Other'];

export type MedSheet =
  /** scanId: the passing scan this dose already has (from Med pass scan), sent with the record. */
  | { mode: 'given'; med: MedPassRow; needs: MedPassNeeds; scanId?: string; timingReason?: string | null; assisted?: boolean }
  | { mode: 'notgiven'; med: MedPassRow; assisted?: boolean }
  | { mode: 'observe'; med: MedPassRow }
  | { mode: 'strike'; med: MedPassRow; administrationId: string }
  /** The server's refusal (POST /med-pass 403): its reason, word for word, and the rule it cites. */
  | { mode: 'refused'; med: MedPassRow; reason: string; citation: { law: string; url: string | null } | null };

/** What the sheet sends back: the extra fields for POST /med-pass, or the strike reason. */
export type MedSheetResult =
  | { kind: 'record'; status: 'GIVEN' | 'MISSED' | 'REFUSED' | 'HELD' | 'OBSERVED'; fields: Record<string, string>; assisted?: boolean }
  | { kind: 'strike'; reason: string; note: string };

const FIELD = 'box-sizing:border-box;width:100%;min-height:34px;padding:0 12px;border:1px solid rgba(34,39,46,.18);border-radius:8px;background:#FBF8F1;font-size:16px;color:#22272E;font-family:inherit;';
const LABEL = 'display:block;font-size:16px;font-weight:700;color:#22272E;margin:10px 0 4px;';

function fieldLabel(need: MedPassNeed, n: MedPassNeeds): string {
  switch (need) {
    case 'preAdmin': return `Reading before giving${n.order.preAdminCheck ? ` (${n.order.preAdminCheck})` : ''}`;
    case 'controlledCount': return 'Count left after this dose (controlled medicine)';
    case 'controlledWitness': return 'Witness (controlled medicine)';
    case 'prnReason': return 'Why it is needed now (as-needed medicine)';
    case 'prnResponse': return 'How the resident responded';
    case 'note': return 'What you saw';
    case 'timingReason': {
      const m = n.order.minutesFromDue;
      // The row's own format ("11h 3m overdue"): hours and minutes, never "663 minutes".
      return m == null ? 'Why it is outside the usual time' : `Why it is ${minsText(Math.abs(m))} ${m > 0 ? 'late' : 'early'}`;
    }
    case 'scan': return 'This building scans every dose. Type or paste what the scanner reads';
    default: return 'Reason';
  }
}

export function MedPassSheet({ sheet, staff, onSubmit, onClose, onScan, onNotGiven }: {
  sheet: MedSheet;
  /** Who is on shift, for a witness. */
  staff: OnShiftPerson[];
  /** Throw to show the error in the sheet. */
  onSubmit: (r: MedSheetResult) => Promise<void>;
  onClose: () => void;
  /** POST /med-pass/scan for this dose (the 'scan' need): the five rights and the hold rules. */
  onScan?: (med: MedPassRow, body: Omit<MedScanBody, 'medicationId'>) => Promise<MedScanResult>;
  /** From a refusal: record the dose as Not given instead (Missed and Refused stay open to the care team). */
  onNotGiven?: (med: MedPassRow) => void;
}) {
  const id = useId();
  const [vals, setVals] = useState<Record<string, string>>({});
  const [pick, setPick] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  // The scan this sheet ran, and the readings a hold rule asked for.
  const [scan, setScan] = useState<MedScanResult | null>(null);
  const [vitals, setVitals] = useState<Partial<Record<HoldVital, string>>>({});
  const firstRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    firstRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const name = sheet.med.name ?? 'this medicine';
  const assisted = (sheet.mode === 'given' || sheet.mode === 'notgiven') && sheet.assisted === true;
  const title = sheet.mode === 'given' ? (assisted ? `Before you record ${name} as assisted` : `Before you record ${name}`)
    : sheet.mode === 'notgiven' ? (assisted ? `Not taken: ${name}` : `Not given: ${name}`)
    : sheet.mode === 'observe' ? `Note on ${name}`
    : sheet.mode === 'refused' ? `Not recorded: ${name}` : `Strike out ${name}`;
  const needs: MedPassNeed[] = sheet.mode === 'given' ? sheet.needs.needs : sheet.mode === 'observe' ? ['note'] : [];
  const filled = (k: MedPassNeed) => (k === 'scan' ? !!(vals.wristbandCode ?? '').trim() && !!(vals.packageCode ?? '').trim() : !!(vals[k] ?? '').trim());
  const ready = sheet.mode === 'given' || sheet.mode === 'observe' ? needs.every(filled) : !!pick;
  // Held per order is the nurse's decision, never an assisted outcome.
  const notGivenReasons = assisted ? NOT_GIVEN.filter((r) => r.status !== 'HELD') : NOT_GIVEN;
  const scanned: Record<string, string> = sheet.mode === 'given' && sheet.scanId ? { scanId: sheet.scanId } : {};
  const go = async (r: MedSheetResult) => {
    setBusy(true);
    setError(null);
    try {
      await onSubmit(r);
      onClose();
    } catch (e) {
      setError(e as ApiError);
      setBusy(false);
    }
  };
  const submit = () => {
    if (!ready || busy) return;
    if (sheet.mode === 'given') {
      const fields: Record<string, string> = {
        ...Object.fromEntries(needs.filter((k) => k !== 'scan').map((k) => [k === 'controlledWitness' ? 'witnessUserId' : k, (vals[k] ?? '').trim()])),
        ...(sheet.timingReason && !needs.includes('timingReason') ? { timingReason: sheet.timingReason } : {}),
        ...scanned,
      };
      if (needs.includes('scan')) return scanThenRecord(fields);
      return go({ kind: 'record', status: 'GIVEN', fields, ...(assisted ? { assisted } : {}) });
    }
    if (sheet.mode === 'notgiven') {
      const r = NOT_GIVEN.find((x) => x.code === pick)!;
      return go({ kind: 'record', status: r.status, fields: { reason: r.code }, ...(assisted ? { assisted } : {}) });
    }
    if (sheet.mode === 'observe') return go({ kind: 'record', status: 'OBSERVED', fields: { note: (vals.note ?? '').trim() } });
    return go({ kind: 'strike', reason: pick, note: note.trim() });
  };
  // The 'scan' need: the codes go to POST /med-pass/scan; only a pass records Given (with the
  // scan's id). A hold offers Held; a block or a missing reading records nothing.
  const scanThenRecord = async (fields: Record<string, string>) => {
    if (!onScan || sheet.mode !== 'given') return;
    setBusy(true);
    setError(null);
    try {
      const v = vitalsOf(vitals);
      const r = await onScan(sheet.med, {
        wristbandCode: (vals.wristbandCode ?? '').trim(),
        packageCode: (vals.packageCode ?? '').trim(),
        ...(Object.keys(v).length ? { vitals: v } : {}),
        ...(fields.timingReason ? { timingReason: fields.timingReason } : {}),
      });
      setScan(r);
      if (r.outcome === 'pass') return go({ kind: 'record', status: 'GIVEN', fields: { ...fields, scanId: r.scanId }, ...(assisted ? { assisted } : {}) });
      setBusy(false);
    } catch (e) {
      setError(e as ApiError);
      setBusy(false);
    }
  };
  const radios = (list: readonly { value: string; label: string }[]) => (
    <div role="radiogroup" aria-label="Reason" style={sx('display:flex;flex-wrap:wrap;gap:6px;margin-top:10px;')}>
      {list.map((o) => (
        <label key={o.value} style={sx(`display:inline-flex;align-items:center;gap:6px;min-height:32px;padding:0 11px;border-radius:8px;font-size:16px;font-weight:700;cursor:pointer;${pick === o.value ? 'color:#FCFAF4;background:#22272E;' : 'color:#22272E;background:#fff;border:1px solid rgba(34,39,46,.2);'}`)}>
          <input type="radio" name={`${id}-r`} value={o.value} checked={pick === o.value} onChange={() => setPick(o.value)} style={sx('margin:0;')} />
          {o.label}
        </label>
      ))}
    </div>
  );

  return (
    <div className="dialog__scrim" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby={`${id}-t`} data-testid="med-sheet">
        <h2 className="dialog__title" id={`${id}-t`}>{title}</h2>
        <div className="dialog__body">
          {sheet.mode === 'given' && (
            <>
              {sheet.needs.order.holdParameter && (
                <span style={sx('display:block;padding:10px 12px;border-radius:8px;background:rgba(34,39,46,.06);font-size:16px;color:#22272E;line-height:1.45;')}>
                  {`The order says: “${sheet.needs.order.holdParameter}”. Following it is the nurse’s decision.`}
                </span>
              )}
              {needs.map((k, i) => (
                <div key={k}>
                  <label className="field__label" htmlFor={`${id}-${k}`} style={sx(LABEL)}>{fieldLabel(k, sheet.needs)}</label>
                  {k === 'scan' ? (
                    <>
                      <input
                        id={`${id}-${k}`}
                        ref={i === 0 ? (el) => { firstRef.current = el; } : undefined}
                        type="text"
                        aria-label="Wristband code"
                        placeholder="Wristband: type or paste the scanned code"
                        autoComplete="off"
                        value={vals.wristbandCode ?? ''}
                        onChange={(e) => setVals((p) => ({ ...p, wristbandCode: e.target.value }))}
                        style={sx(FIELD)}
                      />
                      <input
                        type="text"
                        aria-label="Package code"
                        placeholder="Package: type or paste the scanned code"
                        autoComplete="off"
                        value={vals.packageCode ?? ''}
                        onChange={(e) => setVals((p) => ({ ...p, packageCode: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void submit(); } }}
                        style={sx(FIELD + 'margin-top:6px;')}
                      />
                    </>
                  ) : k === 'controlledWitness' ? (
                    <select
                      id={`${id}-${k}`}
                      ref={i === 0 ? (el) => { firstRef.current = el; } : undefined}
                      value={vals[k] ?? ''}
                      onChange={(e) => setVals((p) => ({ ...p, [k]: e.target.value }))}
                      style={sx(FIELD)}
                    >
                      <option value="">Pick who witnessed it</option>
                      {staff.map((s) => (
                        <option key={s.userId} value={s.userId}>{s.name ?? 'Unnamed'}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={`${id}-${k}`}
                      ref={i === 0 ? (el) => { firstRef.current = el; } : undefined}
                      type="text"
                      value={vals[k] ?? ''}
                      onChange={(e) => setVals((p) => ({ ...p, [k]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void submit(); } }}
                      style={sx(FIELD)}
                    />
                  )}
                </div>
              ))}
              {(scan?.needs ?? []).map((k) => (
                <div key={k}>
                  <label className="field__label" htmlFor={`${id}-v-${k}`} style={sx(LABEL)}>{`${VITAL[k].label} (${VITAL[k].unit})`}</label>
                  <input id={`${id}-v-${k}`} type="text" inputMode="decimal" value={vitals[k] ?? ''} onChange={(e) => setVitals((p) => ({ ...p, [k]: e.target.value }))} style={sx(FIELD)} />
                </div>
              ))}
              {scan && scan.outcome !== 'pass' && scan.reason && (
                <span role="alert" style={sx('display:block;margin-top:10px;padding:10px 12px;border-radius:10px;background:#FBEDEC;border:1px solid rgba(179,38,30,.4);font-size:16px;font-weight:800;color:#8A1C16;line-height:1.4;')}>
                  {scan.reason}
                </span>
              )}
            </>
          )}
          {sheet.mode === 'notgiven' && (
            <>
              <span style={sx('display:block;')}>Pick why. It is recorded with your name and the time.</span>
              {radios(notGivenReasons.map((r) => ({ value: r.code, label: r.label })))}
            </>
          )}
          {sheet.mode === 'observe' && (
            <>
              <span style={sx('display:block;')}>The resident takes this medicine on their own, so nothing needs recording. Add a note only when there is something to say; it is kept with your name and the time.</span>
              <label className="field__label" htmlFor={`${id}-note-obs`} style={sx(LABEL)}>What you saw</label>
              <input
                id={`${id}-note-obs`}
                ref={(el) => { firstRef.current = el; }}
                type="text"
                value={vals.note ?? ''}
                onChange={(e) => setVals((p) => ({ ...p, note: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void submit(); } }}
                style={sx(FIELD)}
              />
            </>
          )}
          {sheet.mode === 'refused' && (
            <>
              <span role="alert" data-testid="med-rule-reason" style={sx('display:block;padding:10px 12px;border-radius:10px;background:#FBEDEC;border:1px solid rgba(179,38,30,.4);font-size:16px;font-weight:800;color:#8A1C16;line-height:1.4;')}>
                {sheet.reason}
              </span>
              {sheet.citation?.url && (
                <a href={sheet.citation.url} target="_blank" rel="noreferrer" style={sx('display:inline-block;margin-top:8px;font-size:16px;color:#2F5C97;')}>
                  {`Read the rule: ${sheet.citation.law}`}
                </a>
              )}
              <span style={sx('display:block;margin-top:8px;font-size:16px;color:#54606C;')}>Nothing was recorded. A nurse can record it, or record it as not given.</span>
            </>
          )}
          {sheet.mode === 'strike' && (
            <>
              <span style={sx('display:block;')}>The original stays on the record, struck through, with your name, the time and the reason.</span>
              {radios(STRIKE.map((r) => ({ value: r, label: r })))}
              <label className="field__label" htmlFor={`${id}-note`} style={sx(LABEL)}>Note (optional)</label>
              <input id={`${id}-note`} type="text" value={note} onChange={(e) => setNote(e.target.value)} style={sx(FIELD)} />
            </>
          )}
        </div>
        {error && <ErrorNote error={error} />}
        <div className="dialog__actions">
          <button type="button" className="btn btn--secondary btn--sm" onClick={onClose}>
            {sheet.mode === 'refused' ? 'Close' : 'Cancel'}
          </button>
          {sheet.mode === 'refused' && onNotGiven && (
            <button type="button" className="btn btn--primary btn--sm" onClick={() => onNotGiven(sheet.med)}>
              Record not given
            </button>
          )}
          {sheet.mode === 'given' && !assisted && (sheet.needs.order.holdParameter || scan?.outcome === 'hold') && (
            <button type="button" className="btn btn--secondary btn--sm" disabled={busy} onClick={() => void go({ kind: 'record', status: 'HELD', fields: { reason: scan?.recordAs?.reason ?? 'held_per_order', ...(scan?.outcome === 'hold' ? { scanId: scan.scanId } : scanned) } })}>
              Not given: held per order
            </button>
          )}
          {sheet.mode !== 'refused' && (
            <button type="button" className={`btn btn--sm ${sheet.mode === 'strike' ? 'btn--danger' : 'btn--primary'}`} onClick={() => void submit()} disabled={busy || !ready}>
              {sheet.mode === 'strike' ? 'Strike out' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
